import { MessageFieldComponent } from 'src/app/shared/message-field/message-field.component';
import { RegisterGoodsService } from './register-goods.service';
import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { RouterService } from 'src/app/services/router/router.service';
import { MaintenanceMenuService } from 'src/app/shared/maintenance-menu/maintenance-menu.service';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { AppAuthService } from 'src/app/services/auth/app-auth.service';

@Component({
  selector: 'app-register-goods',
  templateUrl: './register-goods.component.html',
  styleUrls: ['./register-goods.component.scss']
})
export class RegisterGoodsComponent implements OnInit, AfterViewInit {
  @ViewChild(MessageFieldComponent, { static: false }) messageFieldComponent: MessageFieldComponent;
  @ViewChild('downloadLinkRef') public downloadLinkRef: ElementRef;
  private FileData: NgxFileDropEntry;
  private allowedExtension = '.xlsx';
  /* チェーンリスト */
  public chainList = [];
  /* フィルタリングされたチェーンリスト */
  public filteredChainList: Observable<{}>;
  /* チェーン選択オートコンプリートフォーム */
  public chainListControl = new FormControl();
  /** 商品マスタビューのディレクトリ */
  public tableauGoodsMasterDir: string = '';
  /** Tableauビュー */
  public tableauUrl = '/views/PA/_';
  /** チェーンを含めるか */
  public isIncludeChain:boolean = false;
  /**
   * コンストラクタ
   */
  constructor(
    public maintenanceMenuService: MaintenanceMenuService,
    private routerService: RouterService,
    private registerGoodsService: RegisterGoodsService,
    private toastr: ToastrService,
    private appAuthService: AppAuthService
  ) { }

  ngOnInit(): void {
    this.routerService.setPreviousPage();

    this.registerGoodsService.initialize(environment.organization).subscribe(res => {
      // チェーンリスト取得
      this.chainList = res.chainList;

      // チェーンリストサジェスチョン設定
      this.filteredChainList = this.chainListControl.valueChanges
        .pipe(
          startWith(''),
          map(value => this.autoCompleteFilter(value))
        );

      // チェーンリスト変更時設定
      this.chainListControl.valueChanges.subscribe(x => {
        if (!x) {
          return;
        }
        const chain = this.chainList.find(c => c.chainCd === x);
        if (!chain) {
          return;
        }
        this.tableauGoodsMasterDir = chain.view;
      });
    });
  }

  ngAfterViewInit(): void {
  }

  /**
   * アップロードファイル設定
   * @param file ファイル
   * @returns
   */
  public setFile(file: NgxFileDropEntry) {
    // Excelファイル未選択
    if (file !== null && !file.relativePath.endsWith(this.allowedExtension)) {
      this.toastr.error('Excelファイル以外が選択されています。ファイルを再選択してください", "Excelファイル非選択');
      return;
    }
    this.FileData = file;
  }

  /**
   * ファイルのアップロード処理
   */
  public uploadFile() {
    const formData = new FormData();
    // ファイル未選択
    if (!this.FileData || !this.FileData.fileEntry.isFile) {
      this.toastr.error('商品マスタファイルが選択されていません。', '商品マスタファイル未選択');
      return;
    }

    // チェーン選択
    const chain = this.chainList.find(x => x.chainCd === this.chainListControl.value);
    if (!chain) {
      this.toastr.error('チェーンを選択してください。', 'アンマッチ商品エラー');
      return;
    }
    formData.append('chainCd', chain.chainCd);
    formData.append('organization', environment.organization);

    const fileEntry = this.FileData.fileEntry as FileSystemFileEntry;
    fileEntry.file((file: File) => {
      formData.append('file', file, this.FileData.relativePath);

      this.registerGoodsService.uploadGoodsList(formData).subscribe(res => {
        this.messageFieldComponent.pushMessage(res);
        // エラーある場合ファイルダウンロードせずに終了
        if (res.errorCount > 0) {
          this.toastr.error(
            '商品マスタファイルのアップロードに失敗しました', 'アップロード失敗'
          );
          return;
        }
        this.downloadUpdateFile(res.fileName);
        // 警告あり
        if (res.warningCount > 0) {
          this.toastr.warning(
            '商品マスタファイルのアップロードで警告があります', 'アップロード警告'
          );
        }
        // 正常終了
        else {
          this.toastr.info(
            '商品マスタファイルのアップロードが完了しました', 'アップロード完了'
          );
        }
      });
    });
  }

  /**
   * 指定された商品差分ファイルをダウンロードします
   * @param fileName ファイル名（ファイルパス）
   */
  private downloadUpdateFile(fileName: string) {
    this.registerGoodsService.downloadGoodsList(fileName).subscribe(x => {
      const el = this.downloadLinkRef.nativeElement as HTMLAnchorElement;
      el.download = '商品マスタ変更ファイル.xlsx';
      el.href = URL.createObjectURL(x);
      el.click();
    });
  }

  /**
   * ServiceNowの申請ページを開く
   */
  public applyOnServicenow() {
    // 現状(sprint7)はプロトタイプのディレクトリを指定
    window.open(environment.servicenow.masterMaintenanceURL, '_blank');
  }

  /**
   * ServiceNoの申請履歴ページを開く
   */
  public openApplyList() {
    // 現状(sprint7)はプロトタイプのディレクトリを指定
    const dir = 'sp?id=my_requests';
    window.open(environment.servicenow.myRequestsURL, '_blank');
  }

  /**
   * オートコンプリート表示処理
   */
  public autoCompleteDisplay(value: any) {
    if (!value) {
      return '';
    }
    const chain = this.chainList.find(x => x.chainCd === value);
    return `${chain.chainCd} ${chain.chainName}`;
  }

  /**
   * オートコンプリートフィルタリング
   * @param filteringStr フィルタリングする文字列
   */
  private autoCompleteFilter(filteringStr: any) {
    let filteredKeys = '';
    filteredKeys = filteringStr.toLowerCase();
    const filtered = this.chainList.filter(it => it.chainName.toLowerCase().includes(filteredKeys) || it.chainCd.includes(filteredKeys));
    return filtered;
  }

  /** ダウンロードボタンクリック */
  public async clickedDownLoad() {
    // 入力チェック
    const chainCd = this.chainListControl.value;
    if (!chainCd) {
      this.toastr.error('ダウンロードするチェーンを選択してください。');
      return;
    }
    const chain = this.chainList.find(x => x.chainCd === this.chainListControl.value);

    // ダウンロード開始
    this.registerGoodsService.downloadGoodsMaster({
      chainCd: chain.chainCd,
      userId: await this.appAuthService.userId(),
      organization: environment.organization
    }).subscribe(x => {
      const el = this.downloadLinkRef.nativeElement as HTMLAnchorElement;
      el.download = `商品マスタ_${chain.chainName}.xlsx`;
      el.href = URL.createObjectURL(x);
      el.click();
    });
  }

  /** ダウンロードテンプレートボタンクリック */
  public async clickedDownLoadTemplate() {
    if(this.isIncludeChain && !this.chainListControl.value){
      this.toastr.error("チェーン名が選択されていません。", "チェーン名未選択");
      return;
    }
    const chainObj = this.chainList.find(x => x.chainCd === this.chainListControl.value);
    let chainName = '';
    if(this.isIncludeChain) {
      chainName = chainObj.chainName;
    }

    // ダウンロード開始
    this.registerGoodsService.downloadGoodsMasterTemplate({
      chainCd: chainObj.chainCd,
      organization: environment.organization
    }).subscribe(x => {
      const el = this.downloadLinkRef.nativeElement as HTMLAnchorElement;
      el.download = (!this.isIncludeChain) ? '商品マスタテンプレート.xlsx' : '商品マスタテンプレート_' + chainName + '.xlsx';
      el.href = URL.createObjectURL(x);
      el.click();
    });
  }
}
